import React, { useEffect, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, FormHelperText, Link, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLoadingBackdrop } from '../hooks/useLoadingBackdrop';
import LoadingBackdrop from '../components/LoadingBackdrop';
import { CtaButton, Mono } from '../components/section';
import { brand } from '../thema';
import axios from 'axios';

interface ContactFormProps {
    isMobile?: boolean;
}

// 既存の文言キーは末尾に「*」を含むため、表示時は除いて必須ラベルで示す
const stripMark = (label: string) => label.replace(/\*$/, '');

// 全角数字・各種ハイフンを半角に揃え、空白・括弧を除く
const normalizePhone = (value: string) =>
    value
        .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
        .replace(/[‐－―ー−–—]/g, '-')
        .replace(/[\s　()（）]/g, '');

const phoneDigits = (value: string) => normalizePhone(value).replace(/-/g, '');

// ハイフンの有無は問わず、0から始まる10〜11桁であれば正しい形式とする
const isValidPhone = (value: string) =>
    /^[\d-]+$/.test(normalizePhone(value)) && /^0\d{9,10}$/.test(phoneDigits(value));

// ハイフンが無い場合は桁数から一般的な区切りで付与する（市外局番が4〜5桁の固定電話は 3-3-4 で表示）
const formatPhone = (value: string) => {
    if (!isValidPhone(value)) return value;
    const normalized = normalizePhone(value);
    if (normalized.includes('-')) return normalized;
    const d = phoneDigits(value);
    if (d.length === 11) {
        return /^0800/.test(d) ? `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7)}` : `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    }
    if (/^(0120|0570)/.test(d)) return `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7)}`;
    if (/^0[36]/.test(d)) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 0,
        backgroundColor: brand.paper,
        '& fieldset': { borderColor: brand.line },
        '&:hover fieldset': { borderColor: brand.muted },
        '&.Mui-focused fieldset': { borderColor: brand.ink, borderWidth: 1 },
    },
    '& .MuiFormHelperText-root': { mx: 0 },
};

interface FieldProps {
    id: string;
    label: string;
    required?: boolean;
    hint?: string;
    children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ id, label, required = false, hint, children }) => {
    const { t } = useTranslation();
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography component="label" htmlFor={id} sx={{ fontSize: '0.875rem', fontWeight: 700 }}>
                    {label}
                </Typography>
                <Box
                    component="span"
                    sx={{
                        fontSize: '0.6875rem',
                        lineHeight: 1.6,
                        px: 0.75,
                        border: `1px solid ${required ? brand.accentText : brand.line}`,
                        color: required ? brand.accentText : brand.muted,
                    }}
                >
                    {required ? t('contactPage.required') : t('contactPage.optional')}
                </Box>
            </Box>
            {hint && <Typography sx={{ mb: 1, fontSize: '0.8125rem', lineHeight: 1.7, color: brand.muted }}>{hint}</Typography>}
            {children}
        </Box>
    );
};

const ContactForm: React.FC<ContactFormProps> = () => {
    const { t } = useTranslation();
    const { isLoading, setIsLoading } = useLoadingBackdrop();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        tell_no: '',
        message: '',
        is_policy: false
    });

    const [errors, setErrors] = useState({
        first_name: '',
        last_name: '',
        email: '',
        tell_no: '',
        message: '',
        is_policy: ''
    });
    const [activeStep, setActiveStep] = useState<number>(0);
    const [submitError, setSubmitError] = useState('');
    const topRef = useRef<HTMLDivElement>(null);
    const isFirstRender = useRef(true);

    // ステップ切り替え時はフォームの先頭へ戻す
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        topRef.current?.scrollIntoView({ block: 'start' });
    }, [activeStep]);

    const handleChange = (e: any) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        setErrors({
            ...errors,
            [name]: ''
        });
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // フォーカスが外れたら電話番号にハイフンを付ける
    const handlePhoneBlur = () => {
        setFormData((prev) => ({ ...prev, tell_no: formatPhone(prev.tell_no) }));
    };

    const validateForm = () => {
        const newErrors: any = {};
        if (!formData.first_name) newErrors.first_name = t('errors.first_name_required');
        if (!formData.last_name) newErrors.last_name = t('errors.last_name_required');
        if (!formData.email) newErrors.email = t('errors.email_required');
        else if (!validateEmail(formData.email)) newErrors.email = t('errors.email_invalid');
        if (formData.tell_no && !isValidPhone(formData.tell_no)) newErrors.tell_no = t('errors.phone_invalid');
        if (!formData.message) newErrors.message = t('errors.message_required');
        if (!formData.is_policy) newErrors.is_policy = t('errors.policy_required');
        return newErrors;
    };

    const handleSubmit = async () => {
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            setIsLoading(true);
            setSubmitError('');
            try {
                const API_URL = "https://api.reparu.jp";
                const response = await axios.post(
                    `${API_URL}/contact`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    },
                );

                console.log(response.data);
                setActiveStep(2);
            } catch (error) {
                console.error('Error submitting form:', error);
                setSubmitError(t('contactPage.sendError'));
            } finally {
                setIsLoading(false);
            }
        }
    };

    const nextStep = () => {
        handlePhoneBlur();
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            setActiveStep(1);
        }
    };

    const buckStep = () => {
        setSubmitError('');
        setActiveStep(activeStep - 1);
    };

    const steps = [t('Form Step Input'), t('Form Step Confirm'), t('Form Step Complete')];

    const confirmRows = [
        { label: stripMark(t('Name Label')), value: `${formData.last_name} ${formData.first_name}` },
        { label: stripMark(t('Company Name Label')), value: formData.company_name },
        { label: stripMark(t('Mail Address')), value: formData.email },
        { label: stripMark(t('Telephone')), value: formData.tell_no },
        { label: stripMark(t('Contact')), value: formData.message },
    ];

    return (
        <Box ref={topRef} sx={{ scrollMarginTop: '96px', backgroundColor: brand.paper, border: `1px solid ${brand.line}` }}>
            {/* ステップ表示 */}
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: `1px solid ${brand.line}` }}>
                {steps.map((label, index) => {
                    const active = index === activeStep;
                    const done = index < activeStep;
                    return (
                        <Box
                            component="li"
                            key={label}
                            aria-current={active ? 'step' : undefined}
                            sx={{
                                px: { xs: 1.5, md: 3 },
                                py: 2,
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 1,
                                borderTop: `2px solid ${active ? brand.ink : done ? brand.accent : 'transparent'}`,
                                color: active ? brand.ink : done ? brand.accentText : brand.muted,
                            }}
                        >
                            <Mono color="inherit">{String(index + 1).padStart(2, '0')}</Mono>
                            <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: active ? 700 : 400 }}>{label}</Typography>
                        </Box>
                    );
                })}
            </Box>

            <Box sx={{ p: { xs: 3, md: 5 } }}>
                {activeStep === 0 && (
                    // 入力画面
                    <Box component="form" noValidate onSubmit={(e: React.FormEvent) => { e.preventDefault(); nextStep(); }} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Typography sx={{ fontSize: '0.9375rem', color: brand.inkSub }}>{t('Contact Form Label')}</Typography>

                        <Field id="last_name" label={stripMark(t('Name Label'))} required>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField
                                    id="last_name"
                                    fullWidth
                                    type="text"
                                    name="last_name"
                                    autoComplete="family-name"
                                    value={formData.last_name}
                                    onChange={handleChange}
                                    placeholder={t('Last Name')}
                                    sx={inputSx}
                                    error={Boolean(errors.last_name)}
                                    helperText={errors.last_name}
                                />
                                <TextField
                                    fullWidth
                                    type="text"
                                    name="first_name"
                                    autoComplete="given-name"
                                    value={formData.first_name}
                                    onChange={handleChange}
                                    placeholder={t('First Name')}
                                    inputProps={{ 'aria-label': t('First Name') }}
                                    sx={inputSx}
                                    error={Boolean(errors.first_name)}
                                    helperText={errors.first_name}
                                />
                            </Box>
                        </Field>

                        <Field id="company_name" label={stripMark(t('Company Name Label'))}>
                            <TextField
                                id="company_name"
                                fullWidth
                                type="text"
                                name="company_name"
                                autoComplete="organization"
                                value={formData.company_name}
                                onChange={handleChange}
                                placeholder={t('Company placeholder')}
                                sx={inputSx}
                            />
                        </Field>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                            <Field id="email" label={stripMark(t('Mail Address'))} required>
                                <TextField
                                    id="email"
                                    fullWidth
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="xxx@example.co.jp"
                                    sx={inputSx}
                                    error={Boolean(errors.email)}
                                    helperText={errors.email}
                                />
                            </Field>
                            <Field id="tell_no" label={stripMark(t('Telephone'))} hint={t('contactPage.phoneHint')}>
                                <TextField
                                    id="tell_no"
                                    fullWidth
                                    type="tel"
                                    name="tell_no"
                                    autoComplete="tel"
                                    value={formData.tell_no}
                                    onChange={handleChange}
                                    onBlur={handlePhoneBlur}
                                    inputProps={{ inputMode: 'tel' }}
                                    placeholder="090-1234-5678"
                                    sx={inputSx}
                                    error={Boolean(errors.tell_no)}
                                    helperText={errors.tell_no}
                                />
                            </Field>
                        </Box>

                        <Field id="message" label={stripMark(t('Contact'))} required hint={t('contactPage.messageHint')}>
                            <TextField
                                id="message"
                                fullWidth
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder={t('Contact placeholder')}
                                multiline
                                minRows={8}
                                sx={inputSx}
                                error={Boolean(errors.message)}
                                helperText={errors.message}
                            />
                        </Field>

                        <Box sx={{ pt: 3, borderTop: `1px solid ${brand.line}` }}>
                            <FormControlLabel
                                control={<Checkbox checked={formData.is_policy} onChange={handleChange} name="is_policy" sx={{ '&.Mui-checked': { color: brand.accentText } }} />}
                                label={
                                    <Typography component="span" sx={{ fontSize: '0.875rem' }}>
                                        <Link href="/policy" target="_blank" rel="noopener noreferrer" sx={{ color: brand.ink, textDecorationColor: brand.ink }}>
                                            {stripMark(t('Personal information Label 1'))}
                                        </Link>
                                        {stripMark(t('Personal information Label 2'))}
                                    </Typography>
                                }
                            />
                            {errors.is_policy && (
                                <FormHelperText error>{errors.is_policy}</FormHelperText>
                            )}
                        </Box>

                        <Box>
                            <CtaButton onClick={nextStep}>{t('Confirm Button')}</CtaButton>
                        </Box>
                    </Box>
                )}

                {activeStep === 1 && (
                    // 確認
                    <Box>
                        <Typography sx={{ fontSize: '0.9375rem', color: brand.inkSub }}>{t('Contact Form Label Confirm')}</Typography>
                        <Box component="dl" sx={{ mt: 4, mb: 0, borderTop: `1px solid ${brand.ink}` }}>
                            {confirmRows.map((row) => (
                                <Box key={row.label} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, gap: { xs: 0.5, sm: 3 }, py: 2.5, borderBottom: `1px solid ${brand.line}` }}>
                                    <Box component="dt" sx={{ fontSize: '0.8125rem', fontWeight: 700, color: brand.inkSub }}>{row.label}</Box>
                                    <Box component="dd" sx={{ m: 0, fontSize: '0.9375rem', lineHeight: 1.8, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.value || '—'}</Box>
                                </Box>
                            ))}
                        </Box>
                        {submitError && (
                            <Typography role="alert" sx={{ mt: 3, fontSize: '0.875rem', color: '#C62828' }}>{submitError}</Typography>
                        )}
                        <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <CtaButton variant="secondary" onClick={buckStep}>{t('Buck Button')}</CtaButton>
                            <CtaButton onClick={handleSubmit}>{t('Send Button')}</CtaButton>
                        </Box>
                    </Box>
                )}

                {activeStep === 2 && (
                    // 完了
                    <Box sx={{ py: { xs: 2, md: 4 } }}>
                        <Box sx={{ width: 48, height: 48, border: `1px solid ${brand.accent}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: brand.accentText, fontSize: '1.25rem' }}>
                            ✓
                        </Box>
                        <Typography component="h2" sx={{ mt: 3, fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 700 }}>
                            {t('Contact Form Label Complete1')}
                        </Typography>
                        <Typography sx={{ mt: 2, fontSize: '0.9375rem', lineHeight: 1.9, color: brand.inkSub }}>
                            {t('Contact Form Label Complete2')}
                            <br />
                            {t('Contact Form Label Complete3')}
                        </Typography>
                        <Typography sx={{ mt: 2, fontSize: '0.8125rem', lineHeight: 1.8, color: brand.muted }}>
                            {t('Contact Form Label Complete4')}
                        </Typography>
                        <Box sx={{ mt: 5 }}>
                            <CtaButton href="/" variant="secondary">{t('contactPage.backHome')}</CtaButton>
                        </Box>
                    </Box>
                )}
            </Box>
            <LoadingBackdrop isLoading={isLoading} />
        </Box>
    );
};

export default ContactForm;
